import Highcharts, { SeriesOptionsType } from 'highcharts/highcharts';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import { useRef } from 'react';
import highchartsMore from 'highcharts/highcharts-more';
import { newPalettes } from '../../../themes/newPalette';
import './styles.scss';
import { Grid } from '@mui/material';
import { TABLE_VARIANT, DataTable } from '../../atoms/Table/Table';
import { getAllocationsExhibitTableData } from './utils';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material';
import { Typography } from '../../../components/atoms/Typography/Typography';
import { useCMSContent } from '../../../common/hooks/useCmsContent';

// Chart palette for Highcharts (not React-themed)
const chartPalette = newPalettes['advice'];

// Enables the tag <use> from Icon component
Highcharts.AST.allowedTags.push('use');

highchartsMore(Highcharts);

export type AllocationsOverTimeChartProps = {
    startingYear: number;
    equity: Array<number>;
    fixedIncome: Array<number>;
    alternatives: Array<number>;
    cash: Array<number>;
    isReadOnly?: boolean;
    retirementYear?: number;
    scenarioType?: string;
};
// Type any to allow series options type constraints
const seriesColorMapper: any = {
    Cash: {
        color: chartPalette.teal[500],
        lineColor: chartPalette.teal[400]
    },
    Equity: {
        color: chartPalette.teal[100],
        lineColor: chartPalette.success[500]
    },
    'Fixed Income': {
        color: chartPalette.purple[100],
        lineColor: chartPalette.secondary[500]
    },
    Alternatives: {
        color: chartPalette.primary[100],
        lineColor: chartPalette.info[200]
    }
};

const StyledDisclaimerContent = styled(Typography)(({ theme }) => ({
    fontWeight: 400,
    fontSize: theme.spacing(1.5),
    margin: `${theme.spacing(3)} 0`,
    lineHeight: theme.spacing(2),
    color: theme.palette.grey[400]
}));

export const AllocationsOverTimeChart = ({
    startingYear,
    equity,
    fixedIncome,
    alternatives,
    cash,
    isReadOnly = false,
    retirementYear
}: AllocationsOverTimeChartProps) => {
    const cmsUtil = useCMSContent();
    const cms_projectedAllocationPart1 = cmsUtil.getContentAsElement('plan_detail.projected_allocation.part1');
    const cms_RiskToleranceDescription = cmsUtil.getContentAsString('risk_tolerance.content.disclaimer1');
    const { t } = useTranslation();

    const chartComponentRef = useRef<HighchartsReactRefObject | null>(null);
    const hasAllZeros = (arr: Array<number>) => arr.reduce((val, sum) => val + sum, 0) === 0;
    const generateSeries = () => {
        let series: Array<SeriesOptionsType> = [];
        if (!hasAllZeros(cash))
            series.push({
                type: 'area',
                name: t('CASH'),
                data: cash,
                color: chartPalette.teal[500],
                lineColor: chartPalette.teal[400],
                lineWidth: 2,
                marker: {
                    enabled: false
                },
                animation: !isReadOnly
            });
        if (!hasAllZeros(alternatives))
            series.push({
                name: t('LABEL_ALTERNATIVES'),
                type: 'area',
                data: alternatives,
                color: chartPalette.primary[100],
                lineColor: chartPalette.info[200],
                marker: {
                    enabled: false
                },
                animation: isReadOnly
            });

        if (!hasAllZeros(fixedIncome))
            series.push({
                type: 'area',
                name: t('FIXED INCOME'),
                data: fixedIncome,
                color: chartPalette.purple[100],
                lineColor: chartPalette.secondary[500],
                marker: {
                    enabled: false
                },
                animation: !isReadOnly
            });

        if (!hasAllZeros(equity))
            series.push({
                type: 'area',
                name: t('EQUITY'),
                data: equity,
                fillColor: chartPalette.teal[100],
                lineColor: chartPalette.success[500],
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: true
                        }
                    },
                    symbol: 'circle',
                    fillColor: '#fff',
                    lineColor: chartPalette.info[200],
                    lineWidth: 2,
                    radius: 3
                },
                animation: !isReadOnly
            });

        return series;
    };
    const options: Highcharts.Options = {
        chart: {
            type: 'area'
        },
        title: {
            text: ''
        },
        xAxis: {
            crosshair: {
                dashStyle: 'Dash',
                color: chartPalette.grey[400],
                width: 1,
                zIndex: 10
            },
            lineWidth: 0,
            left: 65,
            min: startingYear,
            allowDecimals: false,
            tickColor: 'rgba(0,0,0,0.5)',
            tickPosition: 'outside',
            labels: {
                style: labelStyle
            }
        },
        yAxis: {
            allowDecimals: false,
            crosshair: false,
            gridLineWidth: 0,
            tickmarkPlacement: 'between',
            tickWidth: 1,
            tickInterval: 10,
            tickColor: 'rgba(0,0,0,0.5)',
            tickPosition: 'outside',
            title: {
                text: null
            },
            labels: {
                distance: 20,
                formatter: function () {
                    if ((Number(this.value) / 10) % 2 === 0) return `${this.value}%`;
                    return '';
                },
                style: labelStyle
            },
            minPadding: 0,
            maxPadding: 0
        },
        tooltip: {
            shared: false,
            split: true,
            outside: true,
            useHTML: true,
            followPointer: false,
            followTouchMove: false,
            backgroundColor: 'transparent',
            borderWidth: 0,

            formatter: function () {
                const { key: year } = this;
                // TODO: Migrate it to a component
                return `
        <div class="tooltip-chart-card">
            <strong class="title">${t('YEAR_TEXT')} ${year}</strong>
            <p class="name">${t('TOTAL_ALLOCATIONS_TEXT')}</p>
            <div>
                ${
                    hasAllZeros(equity)
                        ? ''
                        : `<div class="legend">
                <div class="left">
                    <div
                    class="bar"
                    style="background: ${chartPalette.teal[100]}; border: 1px solid ${chartPalette.success[500]}"
                    ></div>
                    <span class="name">${t('EQUITY')}</span>
                </div>

                <strong class="amount">${equity[Number(this.x) - startingYear] || 0}%</strong>
                </div>`
                }

                ${
                    hasAllZeros(fixedIncome)
                        ? ''
                        : `<div class="legend">
                <div class="left">
                    <div
                    class="bar"
                    style="background: ${chartPalette.purple[100]}; border: 1px solid ${chartPalette.secondary[500]}"
                    ></div>
                    <span class="name">${t('FIXED INCOME')}</span>
                </div>

                <strong class="amount">${fixedIncome[Number(this.x) - startingYear] || 0}%</strong>
                </div>`
                }

                ${
                    hasAllZeros(alternatives)
                        ? ''
                        : `<div class="legend">
                <div class="left">
                    <div
                    class="bar"
                    style="background: ${chartPalette.info[100]}; border: 1px solid ${chartPalette.info[300]}"
                    ></div>
                    <span class="name">${t('LABEL_ALTERNATIVES')} </span>
                </div>

                <strong class="amount">${alternatives[Number(this.x) - startingYear] || 0}%</strong>
                </div>`
                }
                ${
                    hasAllZeros(cash)
                        ? ''
                        : `<div class="legend" style="margin-bottom: 0">
                <div class="left">
                    <div
                    class="bar"
                    style="background: ${chartPalette.teal[500]}; border: 1px solid ${chartPalette.teal[400]}"
                    ></div>
                    <span class="name">${t('CASH')} </span>
                </div>

                <strong class="amount">${cash[Number(this.x) - startingYear] || 0}%</strong>
                </div>`
                }
            </div>
        </div>
            `;
            }
        },
        plotOptions: {
            series: {
                pointStart: startingYear
            },
            area: {
                stacking: 'percent',
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                }
            }
        },
        series: generateSeries(),
        legend: {
            enabled: true,
            align: isReadOnly ? 'left' : 'right',
            verticalAlign: 'top',
            itemMarginBottom: 0,
            itemMarginTop: -10,
            reversed: true,
            symbolPadding: 0,
            symbolWidth: 0,
            symbolHeight: 0,
            squareSymbol: false,
            accessibility: {
                enabled: true,
                keyboardNavigation: {
                    enabled: true
                }
            },
            useHTML: true,
            labelFormatter: function () {
                return `
                <div class="legend-cont">
                    <div
                    class="legend-bar"
                    style="background: ${seriesColorMapper[this.name]?.color}; border: 1px solid ${
                    seriesColorMapper[this.name]?.lineColor
                }"
                    ></div>
                    <p class="legend-name">${this.name} </p>
                </div>`;
            }
        },
        credits: {
            enabled: false
        }
    };

    const { exhibitColumns, exhibitRows } = getAllocationsExhibitTableData({
        startingYear,
        cash,
        equity,
        fixedIncome,
        retirementYear,
        t
    });

    return (
        <>
            {isReadOnly ? (
                <>
                    <HighchartsReact highcharts={Highcharts} options={options} ref={chartComponentRef} />
                    <Grid marginTop={isReadOnly ? 0 : 1}>
                        <DataTable variant={TABLE_VARIANT.EXHIBIT} columns={exhibitColumns} rows={exhibitRows} />
                        <StyledDisclaimerContent
                            label={
                                <>
                                    {' '}
                                    {cms_projectedAllocationPart1}
                                    <br />
                                    {cms_RiskToleranceDescription}
                                </>
                            }
                        />
                    </Grid>
                </>
            ) : (
                <>
                    <HighchartsReact highcharts={Highcharts} options={options} ref={chartComponentRef} />
                    <StyledDisclaimerContent
                        label={
                            <>
                                {cms_projectedAllocationPart1}
                                <br />
                                {cms_RiskToleranceDescription}
                            </>
                        }
                    />
                </>
            )}
        </>
    );
};

const labelStyle = {
    color: chartPalette.neutral.black,
    fontFamily: '"TT Commons Pro"',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '16px',
    letterSpacing: '0.12px'
};
